/**
 * Public Estimate page — what the customer sees when they click the email link.
 *
 * URL: /estimate/:token (no auth)
 *
 * Design notes (v11.2, Feb 2026):
 * • This page is rendered from OUTSIDE the tenant app shell, but the Hines
 *   Time global CSS defaults to dark mode (`--card` is dark slate). Without
 *   an explicit reset, shadcn Card-based markup inherits the dark palette
 *   and the customer sees illegible white-on-near-black text.
 *
 *   Fix: every visible surface uses **inline style** with hard-coded light
 *   palette values pulled from `document_branding` (or the brand defaults).
 *   No shadcn Card / CSS-var-driven utility is used in the content area, so
 *   no dark CSS var can bleed through.
 *
 * • The company `company_logo` field stores **just a filename** (e.g.
 *   `logo_abc.png`). The actual image lives in S3 behind the public
 *   `/api/company/logo/<filename>` endpoint that 302s to a presigned S3
 *   URL. Build the URL accordingly — bare `<img src={filename}>` 404s.
 *
 * • Per-tenant brand palette: `company.document_branding` overlays the
 *   default colours. Same shape used by estimate_pdf.py + invoice_pdf.py
 *   so PDF and web stay visually consistent.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';
import { Loader2, CheckCircle2, XCircle, FileDown, Building2 } from 'lucide-react';
import { useBrandFont } from '../utils/brandFont';

const BACKEND_URL = window.__BACKEND_URL__;
const API = `${BACKEND_URL}/api`;

const DEFAULT_PALETTE = {
  primary_color: '#1E88E5',
  accent_color: '#10B981',
  text_color: '#0F172A',
  muted_color: '#64748B',
  background_color: '#FFFFFF',
  subtle_bg: '#F8FAFC',
  border_color: '#E2E8F0',
};

const CUR_SYM = { USD: 'US$', CAD: 'CA$', EUR: '€', GBP: '£', AUD: 'A$', NZD: 'NZ$', MXN: 'MX$', CHF: 'CHF ', DKK: 'kr ', SEK: 'kr ', NOK: 'kr ' };
let _curCode = 'USD';
const fmtMoney = (n) => `${CUR_SYM[_curCode] || `${_curCode} `}${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }) : '—';

export default function PublicEstimate() {
  const { token } = useParams();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState(null);
  const [showDecline, setShowDecline] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [action, setAction] = useState(null);
  const [busy, setBusy] = useState(false);
  const [logoLoadFailed, setLogoLoadFailed] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/public/estimates/${token}`);
        setDoc(res.data);
        if (res.data.status === 'accepted' || res.data.status === 'converted') setAction('accepted');
        if (res.data.status === 'declined') setAction('declined');
      } catch (e) {
        setError(e?.response?.data?.detail || 'This estimate link is invalid or no longer available.');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const company = doc?.company || {};
  const palette = useMemo(() => ({ ...DEFAULT_PALETTE, ...(company.document_branding || {}) }), [company.document_branding]);
  const brandFont = useBrandFont(company.document_branding, company.id);
  // Logo URL — `company_logo` is just a filename → /api/company/logo/<filename>
  const logoUrl = company.company_logo
    ? (company.company_logo.startsWith('http') || company.company_logo.startsWith('data:')
        ? company.company_logo
        : `${API}/company/logo/${company.company_logo}`)
    : null;

  const handleAccept = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/public/estimates/${token}/accept`);
      setAction('accepted');
      if (res.data.deposit_required) {
        toast({ title: 'Accepted!', description: `Your contractor will contact you to collect the ${fmtMoney(res.data.deposit_amount)} deposit.` });
      } else {
        toast({ title: 'Accepted!', description: 'Your contractor has been notified and will be in touch.' });
      }
    } catch (e) {
      toast({ title: 'Could not accept', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  const handleDecline = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/public/estimates/${token}/decline`, { reason: declineReason });
      setAction('declined');
      setShowDecline(false);
    } catch (e) {
      toast({ title: 'Could not decline', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  // ── Layout helpers (force light theme inline; no CSS-var leakage) ────
  const pageStyle = {
    minHeight: '100vh',
    background: palette.subtle_bg,
    color: palette.text_color,
    fontFamily: brandFont,
    padding: '40px 16px',
  };
  const cardStyle = {
    maxWidth: '768px',
    margin: '0 auto',
    background: palette.background_color,
    color: palette.text_color,
    borderRadius: '12px',
    boxShadow: '0 4px 24px rgba(15,23,42,0.08)',
    border: `1px solid ${palette.border_color}`,
    overflow: 'hidden',
  };

  if (loading) {
    return (
      <div style={pageStyle} className="flex items-center justify-center" data-testid="public-estimate-loading">
        <div style={{ color: palette.muted_color }} className="flex items-center gap-2"><Loader2 className="w-5 h-5 animate-spin" /> Loading estimate…</div>
      </div>
    );
  }

  if (error || !doc) {
    return (
      <div style={pageStyle} className="flex items-center justify-center" data-testid="public-estimate-error">
        <div style={cardStyle} className="p-10 text-center">
          <XCircle className="w-12 h-12 mx-auto mb-4" style={{ color: '#EF4444' }} />
          <p style={{ color: palette.text_color, fontWeight: 600, marginBottom: 4 }}>Estimate unavailable</p>
          <p style={{ color: palette.muted_color, fontSize: 14 }}>{error || 'This estimate may have been deleted, replaced, or never existed.'}</p>
        </div>
      </div>
    );
  }

  const totals = doc.totals || {};
  _curCode = (doc.currency || 'USD').toUpperCase();
  const recipient = doc.customer_id ? null : doc.prospect;
  const expired = doc.valid_until && new Date(doc.valid_until) < new Date();

  // ── After-action confirmation screens ────────────────────────────────
  if (action === 'accepted') {
    return (
      <div style={{ ...pageStyle, background: '#ECFDF5' }} className="flex items-center justify-center" data-testid="public-accept-confirm">
        <div style={cardStyle} className="p-10 text-center">
          <CheckCircle2 className="w-16 h-16 mx-auto mb-4" style={{ color: palette.accent_color }} />
          <h2 style={{ color: palette.text_color, fontSize: 24, fontWeight: 700, marginBottom: 8 }}>Estimate accepted!</h2>
          <p style={{ color: palette.muted_color, marginBottom: 16 }}>Thank you. {company.company_name || 'Your contractor'} has been notified and will reach out to schedule.</p>
          {doc.deposit_required && totals.deposit_amount > 0 && (
            <div style={{ background: '#FEF3C7', border: '1px solid #FCD34D', color: '#92400E', padding: '12px', borderRadius: '8px', fontSize: 14, marginTop: 16 }}>
              <strong>Deposit due: {fmtMoney(totals.deposit_amount)}</strong><br />
              They will contact you to arrange payment.
            </div>
          )}
          <p style={{ color: palette.muted_color, fontSize: 12, marginTop: 24 }}>Reference: {doc.estimate_number}</p>
        </div>
      </div>
    );
  }

  if (action === 'declined') {
    return (
      <div style={pageStyle} className="flex items-center justify-center" data-testid="public-decline-confirm">
        <div style={cardStyle} className="p-10 text-center">
          <XCircle className="w-16 h-16 mx-auto mb-4" style={{ color: palette.muted_color }} />
          <h2 style={{ color: palette.text_color, fontSize: 24, fontWeight: 700, marginBottom: 8 }}>Estimate declined</h2>
          <p style={{ color: palette.muted_color }}>Thanks for letting us know. {company.company_name || 'The contractor'} has been notified.</p>
        </div>
      </div>
    );
  }

  // ── Main estimate view ───────────────────────────────────────────────
  return (
    <div style={pageStyle} data-testid="public-estimate-page">
      {/* Company header */}
      <div style={{ maxWidth: 768, margin: '0 auto 24px' }} className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          {logoUrl && !logoLoadFailed ? (
            <img src={logoUrl} alt={company.company_name || 'Logo'} onError={() => setLogoLoadFailed(true)} style={{ height: 48, maxWidth: 200, objectFit: 'contain' }} />
          ) : (
            <div style={{ width: 48, height: 48, borderRadius: 12, background: palette.primary_color, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#FFFFFF' }}>
              <Building2 className="w-6 h-6" />
            </div>
          )}
          <div>
            <p style={{ color: palette.text_color, fontWeight: 700, fontSize: 18, margin: 0 }}>{company.company_name}</p>
            {company.email && <p style={{ color: palette.muted_color, fontSize: 12, margin: 0 }}>{company.email}</p>}
          </div>
        </div>
        <button
          onClick={() => window.open(`${API}/public/estimates/${token}/pdf`, '_blank')}
          style={{
            background: palette.background_color, color: palette.text_color,
            border: `1px solid ${palette.border_color}`, padding: '8px 14px',
            borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 6,
          }}
          data-testid="public-download-pdf"
        >
          <FileDown className="w-4 h-4" /> Download PDF
        </button>
      </div>

      <div style={cardStyle}>
        {/* Brand band */}
        <div style={{ background: palette.primary_color, height: 6 }} />

        <div style={{ padding: 32 }}>
          {/* Estimate heading */}
          <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
            <div>
              <p style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: palette.muted_color, marginBottom: 4 }}>Estimate</p>
              <h1 style={{ fontSize: 30, fontWeight: 700, color: palette.text_color, margin: 0 }} data-testid="public-estimate-number">{doc.estimate_number}</h1>
              <p style={{ color: palette.text_color, marginTop: 4 }}>{doc.title}</p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <p style={{ color: palette.muted_color, fontSize: 13, margin: 0 }}>Valid until</p>
              <p style={{ fontWeight: 600, fontSize: 14, color: expired ? '#DC2626' : palette.text_color, margin: 0 }} data-testid="public-valid-until">{fmtDate(doc.valid_until)}</p>
              {expired && <p style={{ color: '#DC2626', fontSize: 12, marginTop: 4 }}>This estimate has expired.</p>}
            </div>
          </div>

          {/* Recipient + job site */}
          <div style={{ borderBottom: `1px solid ${palette.border_color}`, paddingBottom: 20, marginBottom: 20 }} className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <p style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: palette.muted_color, marginBottom: 4 }}>Prepared for</p>
              <p style={{ fontWeight: 600, color: palette.text_color, margin: 0 }}>{recipient?.name || '—'}</p>
              {recipient?.email && <p style={{ color: palette.text_color, margin: 0 }}>{recipient.email}</p>}
              {recipient?.address && <p style={{ color: palette.text_color, margin: 0 }}>{recipient.address}</p>}
            </div>
            {doc.job_address && (
              <div>
                <p style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: palette.muted_color, marginBottom: 4 }}>Job site</p>
                <p style={{ color: palette.text_color, margin: 0 }}>{doc.job_address}</p>
              </div>
            )}
          </div>

          {/* Scope */}
          {doc.description && (
            <div style={{ borderBottom: `1px solid ${palette.border_color}`, paddingBottom: 20, marginBottom: 20 }}>
              <p style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: palette.muted_color, marginBottom: 8 }}>Scope of work</p>
              <p style={{ color: palette.text_color, whiteSpace: 'pre-wrap', fontSize: 14, margin: 0 }}>{doc.description}</p>
            </div>
          )}

          {/* Pricing breakdown (hidden for lump-sum estimates) */}
          {doc.presentation === 'lump_sum' && (
            <div style={{ borderBottom: `1px solid ${palette.border_color}`, paddingBottom: 20, marginBottom: 20 }} data-testid="public-lump-sum-summary">
              <p style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: palette.muted_color, marginBottom: 8 }}>This price includes</p>
              <p style={{ color: palette.text_color, whiteSpace: 'pre-wrap', fontSize: 14, margin: 0 }}>{doc.summary_included || 'All labour, equipment and materials required to complete the described service.'}</p>
            </div>
          )}
          <p style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: palette.muted_color, marginBottom: 12 }}>{doc.presentation === 'lump_sum' ? `Price (${doc.currency || 'USD'})` : `Pricing breakdown (${doc.currency || 'USD'})`}</p>
          <table style={{ width: '100%', fontSize: 14, marginBottom: 24, borderCollapse: 'collapse' }}>
            <tbody>
              {(doc.presentation === 'lump_sum' ? [] : [
                ['Labour', totals.labour_total],
                ['Materials', totals.materials_total],
                ['Vehicle / mileage', totals.vehicle_total],
                ['Fuel / energy surcharge', totals.fuel_total],
                ['Equipment', totals.equipment_total],
                ['Disposal', totals.disposal_total],
                ['Other', totals.custom_total],
              ]).filter(([_, v]) => v > 0).map(([label, v]) => (
                <tr key={label} style={{ borderBottom: `1px solid ${palette.subtle_bg}` }}>
                  <td style={{ padding: '8px 0', color: palette.text_color }}>{label}</td>
                  <td style={{ padding: '8px 0', textAlign: 'right', fontWeight: 500, color: palette.text_color }}>{fmtMoney(v)}</td>
                </tr>
              ))}
              <tr>
                <td style={{ paddingTop: 12, color: palette.text_color }}>Subtotal</td>
                <td style={{ paddingTop: 12, textAlign: 'right', fontWeight: 600, color: palette.text_color }}>{fmtMoney(totals.subtotal)}</td>
              </tr>
              {totals.tax_total > 0 && (
                <tr>
                  <td style={{ padding: '4px 0', color: palette.text_color }}>Tax</td>
                  <td style={{ padding: '4px 0', textAlign: 'right', fontWeight: 600, color: palette.text_color }}>{fmtMoney(totals.tax_total)}</td>
                </tr>
              )}
              <tr>
                <td style={{ paddingTop: 12, fontSize: 18, fontWeight: 700, color: palette.text_color }}>Total</td>
                <td style={{ paddingTop: 12, textAlign: 'right', fontSize: 26, fontWeight: 700, color: palette.primary_color }} data-testid="public-grand-total">{fmtMoney(totals.grand_total)}</td>
              </tr>
              {doc.deposit_required && totals.deposit_amount > 0 && (
                <tr>
                  <td style={{ paddingTop: 8, color: palette.accent_color, fontWeight: 600 }}>Deposit on acceptance ({doc.deposit_pct}%)</td>
                  <td style={{ paddingTop: 8, textAlign: 'right', color: palette.accent_color, fontWeight: 700 }} data-testid="public-deposit-amount">{fmtMoney(totals.deposit_amount)}</td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Terms */}
          <div style={{ background: palette.subtle_bg, borderRadius: 8, padding: 16, fontSize: 12, color: palette.muted_color, marginBottom: 24, lineHeight: 1.6 }}>
            <p style={{ fontWeight: 600, color: palette.text_color, marginBottom: 8 }}>Terms &amp; conditions</p>
            <ul style={{ listStyle: 'disc', paddingLeft: 20, margin: 0 }}>
              <li>This document is an estimate, not a bill. No work has been performed.</li>
              <li>Pricing is valid until {fmtDate(doc.valid_until)}. Quoted pricing may change after that date.</li>
              {doc.deposit_required && totals.deposit_amount > 0 && (
                <li>A non-refundable deposit of <strong>{fmtMoney(totals.deposit_amount)} ({doc.deposit_pct}%)</strong> is required upon acceptance. The remaining balance is due upon completion.</li>
              )}
              <li>Final invoiced amount may vary if scope changes are agreed to in writing.</li>
              {company.estimate_terms && company.estimate_terms.split('\n').map((line, i) => line.trim() && <li key={i}>{line.trim()}</li>)}
            </ul>
          </div>

          {/* Actions */}
          {!expired ? (
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleAccept}
                disabled={busy}
                style={{
                  flex: 1, padding: '14px 24px', borderRadius: 8, fontSize: 16, fontWeight: 600,
                  background: palette.accent_color, color: '#FFFFFF', border: 'none',
                  cursor: busy ? 'wait' : 'pointer', display: 'inline-flex',
                  alignItems: 'center', justifyContent: 'center', gap: 8,
                  opacity: busy ? 0.7 : 1, transition: 'opacity 0.2s',
                }}
                data-testid="public-accept-btn"
              >
                {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle2 className="w-5 h-5" />}
                Accept estimate
              </button>
              <button
                onClick={() => setShowDecline(true)}
                disabled={busy}
                style={{
                  flex: 1, padding: '14px 24px', borderRadius: 8, fontSize: 16, fontWeight: 600,
                  background: palette.background_color, color: palette.text_color,
                  border: `2px solid ${palette.border_color}`,
                  cursor: busy ? 'wait' : 'pointer', display: 'inline-flex',
                  alignItems: 'center', justifyContent: 'center', gap: 8,
                }}
                data-testid="public-decline-btn"
              >
                <XCircle className="w-5 h-5" /> Decline
              </button>
            </div>
          ) : (
            <p style={{ color: '#DC2626', fontWeight: 600, textAlign: 'center', padding: '16px 0' }}>
              This estimate has expired. Please contact {company.company_name} for an updated quote.
            </p>
          )}

          {showDecline && (
            <div style={{ marginTop: 24, padding: 16, background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8 }} data-testid="public-decline-form">
              <p style={{ fontWeight: 600, color: palette.text_color, marginBottom: 8, fontSize: 14 }}>Help us improve — why are you declining?</p>
              <textarea
                rows={3}
                value={declineReason}
                onChange={(e) => setDeclineReason(e.target.value)}
                placeholder="(Optional)"
                style={{
                  width: '100%', padding: 8, borderRadius: 6,
                  border: `1px solid ${palette.border_color}`, fontSize: 14,
                  fontFamily: 'inherit', resize: 'vertical', color: palette.text_color,
                  background: palette.background_color,
                }}
                data-testid="decline-reason-input"
              />
              <div className="flex gap-2 mt-3">
                <button
                  onClick={handleDecline}
                  disabled={busy}
                  style={{
                    background: '#DC2626', color: '#FFFFFF', border: 'none',
                    padding: '8px 14px', borderRadius: 6, fontSize: 14, fontWeight: 500,
                    cursor: busy ? 'wait' : 'pointer',
                  }}
                  data-testid="decline-confirm-btn"
                >
                  {busy ? 'Submitting…' : 'Confirm decline'}
                </button>
                <button
                  onClick={() => setShowDecline(false)}
                  style={{
                    background: palette.background_color, color: palette.text_color,
                    border: `1px solid ${palette.border_color}`,
                    padding: '8px 14px', borderRadius: 6, fontSize: 14,
                  }}
                >Cancel</button>
              </div>
            </div>
          )}
        </div>
      </div>

      <p style={{ textAlign: 'center', color: palette.muted_color, fontSize: 11, marginTop: 24 }}>Powered by Hines Time</p>
    </div>
  );
}
